import { useEffect } from 'react';
import { useTenant } from './useTenant';
import { useCemeteryBranding, useCemeteryUIConfig } from './useCemeteryConfig';

/**
 * Hook that applies comprehensive tenant-specific theming
 */
export function useTheme() {
  const { tenant, isDemoMode } = useTenant();
  const { data: branding, isLoading: brandingLoading } = useCemeteryBranding(!!tenant?.id);
  const { data: uiConfig, isLoading: uiLoading } = useCemeteryUIConfig(!!tenant?.id);

  useEffect(() => {
    const root = document.documentElement;

    if (!tenant || isDemoMode || brandingLoading || !branding) {
      // Reset to default theme for demo mode
      applyDefaultTheme(root);
      return;
    }

    // Apply comprehensive tenant theming
    applyTenantTheme(root, tenant, branding, uiConfig);
  }, [tenant, isDemoMode, branding, brandingLoading, uiConfig, uiLoading]);

  return {
    primaryColor: branding?.primary_color || tenant?.branding?.primaryColor || '#EA6D2C',
    secondaryColor: branding?.secondary_color_1 || tenant?.branding?.secondaryColor || '#F5F1E8',
    backgroundColor: branding?.background_color_primary || tenant?.branding?.backgroundColor || '#FAFAFA',
    textColor: branding?.text_color_primary || tenant?.branding?.textColor || '#1F2937',
    logoUrl: branding?.logo_url || tenant?.branding?.logoUrl,
    faviconUrl: branding?.favicon_url || tenant?.branding?.faviconUrl,
    isLoading: brandingLoading || uiLoading,
  };
}

/**
 * Apply default theme variables
 */
function applyDefaultTheme(root: HTMLElement) {
  // Colors
  root.style.setProperty('--primary-color', '#D4551B');
  root.style.setProperty('--secondary-color', '#F5F1E8');
  root.style.setProperty('--text-primary', '#1F2937');
  root.style.setProperty('--color-primary', '#D4551B');
  root.style.setProperty('--color-secondary', '#F5F1E8');
  root.style.setProperty('--color-accent', '#B8441A');
  root.style.setProperty('--color-background', '#FAFAFA');
  root.style.setProperty('--color-text', '#1F2937');

  // RGB versions for Tailwind opacity
  root.style.setProperty('--color-primary-rgb', '234, 109, 44');
  root.style.setProperty('--color-secondary-rgb', '245, 241, 232');
  root.style.setProperty('--color-accent-rgb', '182, 71, 25');
  root.style.setProperty('--color-background-rgb', '250, 250, 250');
  root.style.setProperty('--color-text-rgb', '31, 41, 55');

  // Fonts
  root.style.setProperty('--font-heading', 'Merriweather, serif');
  root.style.setProperty('--font-body', 'Open Sans, sans-serif');

  // Typography
  root.style.setProperty('--text-base', '16px');
  root.style.setProperty('--text-large', '18px');
  root.style.setProperty('--text-small', '14px');

  // Spacing
  root.style.setProperty('--spacing-base', '16px');
  root.style.setProperty('--spacing-gap', '24px');

  // Layout
  root.style.setProperty('--max-width', '1200px');
  root.style.setProperty('--header-height', '60px');

  // Components
  root.style.setProperty('--border-radius', '8px');

  // Document
  document.title = 'Sanctuary - Cemetery Services';
}

/**
 * Apply comprehensive tenant theming
 */
function applyTenantTheme(root: HTMLElement, tenant: any, branding: any, uiConfig?: any) {
  if (!branding) return;

  // === COMPREHENSIVE COLOR SYSTEM ===
  root.style.setProperty('--color-primary', branding.primary_color);
  root.style.setProperty('--color-primary-hover', branding.primary_color_hover);
  root.style.setProperty('--color-secondary-1', branding.secondary_color_1);
  root.style.setProperty('--color-secondary-2', branding.secondary_color_2);
  root.style.setProperty('--color-secondary-3', branding.secondary_color_3);
  root.style.setProperty('--color-secondary-4', branding.secondary_color_4);
  root.style.setProperty('--color-secondary-5', branding.secondary_color_5);
  root.style.setProperty('--color-accent', branding.accent_color);
  root.style.setProperty('--color-bg-primary', branding.background_color_primary);
  root.style.setProperty('--color-bg-secondary', branding.background_color_secondary);
  root.style.setProperty('--color-text-primary', branding.text_color_primary);
  root.style.setProperty('--color-text-secondary', branding.text_color_secondary);
  root.style.setProperty('--color-text-light', branding.text_color_light);

  // Legacy support
  root.style.setProperty('--primary-color', branding.primary_color);
  root.style.setProperty('--secondary-color', branding.secondary_color_1);
  root.style.setProperty('--text-primary', branding.text_color_primary);
  root.style.setProperty('--color-secondary', branding.secondary_color_1);
  root.style.setProperty('--color-background', branding.background_color_primary);
  root.style.setProperty('--color-text', branding.text_color_primary);

  // === FIXED UI COLORS ===
  // Gray scale
  root.style.setProperty('--color-gray-50', '#F9FAFB');
  root.style.setProperty('--color-gray-100', '#F3F4F6');
  root.style.setProperty('--color-gray-200', '#E5E7EB');
  root.style.setProperty('--color-gray-300', '#D1D5DB');
  root.style.setProperty('--color-gray-400', '#9CA3AF');
  root.style.setProperty('--color-gray-500', '#6B7280');
  root.style.setProperty('--color-gray-600', '#4B5563');
  root.style.setProperty('--color-gray-700', '#374151');
  root.style.setProperty('--color-gray-800', '#1F2937');
  root.style.setProperty('--color-gray-900', '#111827');
  
  // Semantic colors
  root.style.setProperty('--color-success', '#10B981');
  root.style.setProperty('--color-error', '#EF4444');
  root.style.setProperty('--color-warning', '#F59E0B');
  root.style.setProperty('--color-info', '#3B82F6');
  
  // RGB versions for Tailwind opacity
  root.style.setProperty('--color-gray-50-rgb', '249, 250, 251');
  root.style.setProperty('--color-gray-100-rgb', '243, 244, 246');
  root.style.setProperty('--color-gray-200-rgb', '229, 231, 235');
  root.style.setProperty('--color-gray-300-rgb', '209, 213, 219');
  root.style.setProperty('--color-gray-400-rgb', '156, 163, 175');
  root.style.setProperty('--color-gray-500-rgb', '107, 114, 128');
  root.style.setProperty('--color-gray-600-rgb', '75, 85, 99');
  root.style.setProperty('--color-gray-700-rgb', '55, 65, 81');
  root.style.setProperty('--color-gray-800-rgb', '31, 41, 55');
  root.style.setProperty('--color-gray-900-rgb', '17, 24, 39');
  
  root.style.setProperty('--color-success-rgb', '16, 185, 129');
  root.style.setProperty('--color-error-rgb', '239, 68, 68');
  root.style.setProperty('--color-warning-rgb', '245, 158, 11');
  root.style.setProperty('--color-info-rgb', '59, 130, 246');
  
  // Convert hex to RGB for Tailwind opacity utilities
  const primaryRgb = hexToRgb(branding.primary_color);
  const secondaryRgb = hexToRgb(branding.secondary_color_1);
  const accentRgb = hexToRgb(branding.accent_color);
  const backgroundRgb = hexToRgb(branding.background_color_primary);
  const textRgb = hexToRgb(branding.text_color_primary);

  if (primaryRgb) {
    root.style.setProperty('--color-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
    generateColorVariations(root, 'primary', primaryRgb);
  }
  if (secondaryRgb) {
    root.style.setProperty('--color-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
    generateColorVariations(root, 'secondary', secondaryRgb);
  }
  if (accentRgb) {
    root.style.setProperty('--color-accent-rgb', `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`);
    generateColorVariations(root, 'accent', accentRgb);
  }
  if (backgroundRgb) {
    root.style.setProperty('--color-background-rgb', `${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}`);
  }
  if (textRgb) {
    root.style.setProperty('--color-text-rgb', `${textRgb.r}, ${textRgb.g}, ${textRgb.b}`);
  }

  // === FONTS ===
  // Load Google Fonts dynamically if URLs provided
  if (branding.font_heading_url) {
    loadGoogleFont(branding.font_heading_url);
  }
  if (branding.font_body_url) {
    loadGoogleFont(branding.font_body_url);
  }
  
  // Set font families (fallback to existing logic)
  if (tenant?.branding?.fonts?.heading) {
    root.style.setProperty('--font-heading', tenant.branding.fonts.heading);
  }
  if (tenant?.branding?.fonts?.body) {
    root.style.setProperty('--font-body', tenant.branding.fonts.body);
  }

  // === UI CONFIGURATION ===
  if (uiConfig) {
    // Border radius
    root.style.setProperty('--border-radius', uiConfig.border_radius || '8px');
    
    // Animation speed
    const animationDuration = {
      slow: '300ms',
      normal: '200ms',
      fast: '100ms'
    }[uiConfig.animation_speed] || '200ms';
    root.style.setProperty('--animation-duration', animationDuration);
    
    // Typography
    if (tenant?.uiConfig?.fontSize) {
      root.style.setProperty('--text-base', tenant.uiConfig.fontSize.base);
      root.style.setProperty('--text-large', tenant.uiConfig.fontSize.large);
      root.style.setProperty('--text-small', tenant.uiConfig.fontSize.small);
    }

    // Spacing
    if (tenant?.uiConfig?.spacing) {
      root.style.setProperty('--spacing-base', tenant.uiConfig.spacing.base);
      root.style.setProperty('--spacing-gap', tenant.uiConfig.spacing.gap);
    }

    // Layout
    if (tenant?.uiConfig?.layout) {
      root.style.setProperty('--max-width', tenant.uiConfig.layout.maxWidth);
      root.style.setProperty('--header-height', tenant.uiConfig.layout.headerHeight);
    }

    // Components
    if (tenant?.uiConfig?.components) {
      root.style.setProperty('--border-radius', tenant.uiConfig.components.cornerRadius);
      
      // Button sizes
      const buttonSizes = {
        small: '32px',
        medium: '40px',
        large: '48px'
      };
      root.style.setProperty('--button-height', buttonSizes[tenant.uiConfig.components.buttonSize] || buttonSizes.medium);
    }
  }

  // === DOCUMENT UPDATES ===
  // Update document title
  document.title = `${tenant.name} - Cemetery Services`;

  // Update favicon
  if (branding.favicon_url) {
    updateFavicon(branding.favicon_url);
  }

  // Apply theme class to body
  document.body.className = `theme-${tenant.subdomain}`;
}

/**
 * Convert hex color to RGB object
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

/**
 * Generate color variations with opacity levels
 */
function generateColorVariations(root: HTMLElement, colorName: string, rgb: { r: number; g: number; b: number }) {
  const opacities = [10, 20, 30, 40, 50, 60, 70, 80, 90];
  
  opacities.forEach(opacity => {
    root.style.setProperty(
      `--color-${colorName}-${opacity}`,
      `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.${opacity.toString().padStart(2, '0')})`
    );
  });

  // Generate lighter and darker variations
  const variations = {
    50: lightenColor(rgb, 0.4),
    100: lightenColor(rgb, 0.3),
    200: lightenColor(rgb, 0.2),
    300: lightenColor(rgb, 0.1),
    400: lightenColor(rgb, 0.05),
    500: rgb, // Base color
    600: darkenColor(rgb, 0.1),
    700: darkenColor(rgb, 0.2),
    800: darkenColor(rgb, 0.3),
    900: darkenColor(rgb, 0.4),
  };

  Object.entries(variations).forEach(([shade, color]) => {
    root.style.setProperty(
      `--color-${colorName}-${shade}`,
      `${color.r}, ${color.g}, ${color.b}`
    );
  });
}

/**
 * Lighten a color by a factor
 */
function lightenColor(rgb: { r: number; g: number; b: number }, factor: number) {
  return {
    r: Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor)),
    g: Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor)),
    b: Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor)),
  };
}

/**
 * Darken a color by a factor
 */
function darkenColor(rgb: { r: number; g: number; b: number }, factor: number) {
  return {
    r: Math.max(0, Math.round(rgb.r * (1 - factor))),
    g: Math.max(0, Math.round(rgb.g * (1 - factor))),
    b: Math.max(0, Math.round(rgb.b * (1 - factor))),
  };
}

/**
 * Update favicon dynamically
 */
function updateFavicon(faviconUrl: string) {
  // Remove existing favicon
  const existingFavicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
  if (existingFavicon) {
    existingFavicon.remove();
  }

  // Add new favicon
  const newFavicon = document.createElement('link');
  newFavicon.rel = 'icon';
  newFavicon.type = 'image/png';
  newFavicon.href = faviconUrl;
  document.head.appendChild(newFavicon);
}

/**
 * Load Google Font dynamically
 */
function loadGoogleFont(fontUrl: string) {
  // Check if font is already loaded
  if (document.querySelector(`link[href="${fontUrl}"]`)) {
    return;
  }
  
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = fontUrl;
  document.head.appendChild(link);
}